import React, { useEffect, useState } from 'react';
import { Box, Chip, Paper, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import dayjs from 'dayjs';
import api from '../../utils/functions/api';

const API = `${process.env.REACT_APP_API_URL}/ai-followup`;

const STAGE_LABEL = { snapshot: 'เก็บข้อมูล', analyze: 'AI วิเคราะห์', alert: 'สร้างรายงานผู้บริหาร' };
const STATUS_META = {
  done: { label: 'เสร็จ', bg: '#dcfce7', color: '#166534' },
  running: { label: 'กำลังทำ', bg: '#dbeafe', color: '#1e40af' },
  failed: { label: 'ล้มเหลว', bg: '#fee2e2', color: '#991b1b' },
};

// สถานะการรันและค่าใช้จ่าย AI — ให้เห็นว่าระบบทำงานทุกวันจริงไหม ใช้ AI ตัวไหน เสียเงินเท่าไร
function AiRunStatus() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get(`${API}/run-status`, { params: { days: 14 } }).then((res) => setData(res.data)).catch(() => setData(null));
  }, []);

  if (!data) return <Typography color="text.secondary">กำลังโหลด...</Typography>;

  return (
    <Box>
      <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap">
        <Chip size="small" label={data.enabled ? 'ระบบเปิดทำงาน' : 'ระบบปิดอยู่ (AI_FOLLOWUP ไม่ได้เปิดในเซิร์ฟเวอร์นี้)'} color={data.enabled ? 'success' : 'default'} />
        <Chip size="small" label={`งบ AI แบบเสียเงินสูงสุด ${data.daily_budget_baht} บาท/วัน`} />
        <Chip size="small" label={data.question_review ? 'หัวหน้าตรวจคำถามก่อนแสดง: เปิด' : 'หัวหน้าตรวจคำถามก่อนแสดง: ปิด'} />
        {data.latest_report && (
          <Chip size="small" label={`รายงานล่าสุด ${data.latest_report.date} ฉบับที่ ${data.latest_report.version} (${data.latest_report.phase === 'updated' ? 'ปรับปรุง' : 'เบื้องต้น'})`} />
        )}
      </Stack>

      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>การรันแต่ละวัน</Typography>
      <Paper variant="outlined" sx={{ mb: 3, overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>วันที่ข้อมูล</TableCell>
              <TableCell>ขั้นตอน</TableCell>
              <TableCell>สถานะ</TableCell>
              <TableCell align="right">ทำแล้ว/ทั้งหมด</TableCell>
              <TableCell align="right">ล้มเหลว</TableCell>
              <TableCell align="right">ค่าใช้จ่าย (บาท)</TableCell>
              <TableCell>เริ่ม</TableCell>
              <TableCell>ข้อผิดพลาดล่าสุด</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.runs.length === 0 && (
              <TableRow><TableCell colSpan={8}>ยังไม่มีบันทึกการรันในช่วงนี้</TableCell></TableRow>
            )}
            {data.runs.map((r) => {
              const st = STATUS_META[r.status] || STATUS_META.running;
              return (
                <TableRow key={`${r.date}-${r.stage}`}>
                  <TableCell>{r.date}</TableCell>
                  <TableCell>{STAGE_LABEL[r.stage] || r.stage}</TableCell>
                  <TableCell><Chip size="small" label={st.label} sx={{ bgcolor: st.bg, color: st.color }} /></TableCell>
                  <TableCell align="right">{`${r.processed}/${r.total}`}</TableCell>
                  <TableCell align="right" sx={{ color: r.failed ? '#b91c1c' : 'inherit' }}>{r.failed}</TableCell>
                  <TableCell align="right">{(r.cost_baht || 0).toFixed(2)}</TableCell>
                  <TableCell>{r.started_at ? dayjs(r.started_at).format('D/M HH:mm') : '-'}</TableCell>
                  <TableCell sx={{ maxWidth: 260, color: '#b91c1c' }}>{r.error}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Paper>

      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>การใช้ AI แยกตามตัวที่ใช้</Typography>
      <Paper variant="outlined" sx={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>วันที่ข้อมูล</TableCell>
              <TableCell>AI</TableCell>
              <TableCell>โมเดล</TableCell>
              <TableCell>ผล</TableCell>
              <TableCell align="right">จำนวนครั้ง</TableCell>
              <TableCell align="right">ค่าใช้จ่ายประมาณ (บาท)</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.usage.length === 0 && (
              <TableRow><TableCell colSpan={6}>ยังไม่มีการวิเคราะห์ในช่วงนี้</TableCell></TableRow>
            )}
            {data.usage.map((u) => (
              <TableRow key={`${u.date}-${u.provider}-${u.model}-${u.status}`}>
                <TableCell>{u.date}</TableCell>
                <TableCell>{u.provider || '-'}</TableCell>
                <TableCell>{u.model || '-'}</TableCell>
                <TableCell>{u.status === 'failed' ? 'ล้มเหลว' : 'สำเร็จ'}</TableCell>
                <TableCell align="right">{u.count}</TableCell>
                <TableCell align="right">{u.cost_baht.toFixed(2)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
        ค่าใช้จ่ายเป็นตัวเลขประมาณจากจำนวนตัวอักษร เฉพาะ AI แบบเสียเงิน; AI แบบฟรีนับเป็น 0
      </Typography>
    </Box>
  );
}

export default AiRunStatus;
